import React, { useState, useCallback, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Fade,
  useMediaQuery,
  Snackbar,
  Alert
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Receipt,
  Users,
  Brain,
  TrendingUp,
  Star,
  Building2,
  Plus,
  Package,
  DollarSign,
  Calendar,
  MessageCircle,
  Upload,
  Download,
  PieChart,
  ArrowUpDown,
  UserPlus,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  ShoppingCart,
  Store as StoreIcon,
  ChartScatter,
  Clock,
  Wand2,
  Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop, useDragLayer } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';

import AGTProduct from './products/AGTProduct';
import AGTCashFlow from './products/AGTCashFlow';
import AGTMarketplace from './products/AGTMarketplace';
import AGTCouncil from './products/AGTCouncil';
import AGTGrowth from './products/AGTGrowth';
import AGTProfile from './products/AGTProfile';
import AGTFound from './extras/AGTFound';
import AGTFoundResults from './extras/AGTFoundResults';
import SubscriptionGateway from './Subscription/SubscriptionGateway';

// ============= Importación de shortcuts existentes =============
import SalesStrategyShortcut from './shortcuts/SalesStrategyShortcut';
import ProductShortcut from './shortcuts/ProductShortcut';
import CheckInventoryShortcut from './shortcuts/CheckInventoryShortcut';
import ReviewPricesShortcut from './shortcuts/ReviewPricesShortcut';

// ============= Importación de shortcuts nuevos =============
import TransactionsShortcut from './shortcuts/TransactionsShortcut';
import QuickUploadShortcut from './shortcuts/QuickUploadShortcut';
import CurrentFlowShortcut from './shortcuts/CurrentFlowShortcut';
import ViewBudgetsShortcut from './shortcuts/ViewBudgetsShortcut';

// ============= Importación de tus 4 atajos de Marketplace =============
import ActiveDealsShortcut from './shortcuts/ActiveDealsShortcut';
import NewSupplierShortcut from './shortcuts/NewSupplierShortcut';
import StoreShortcut from './shortcuts/StoreShortcut';
import StatsShortcut from './shortcuts/StatsShortcut';

// ============= Importación de los nuevos atajos de Council =============
import RecentConsultationsShortcut from './shortcuts/RecentConsultationsShortcut';
import ConsultationStatsShortcut from './shortcuts/ConsultationStatsShortcut';
import ScheduledSessionsShortcut from './shortcuts/ScheduledSessionsShortcut';
import ScheduleMeetingShortcut from './shortcuts/ScheduleMeetingShortcut';

import AppleSpinner from '../common/AppleSpinner';

// Contexto de productos
import { ProductContext } from '../../context/ProductContext';

// Importar servicios
import profileService from './services/AGTUps-product-services/profileService';
import subscriptionService from './services/AGTUps-product-services/subscription-service';

// Detector de dispositivo táctil mejorado
const isTouchDevice = () => {
  if (typeof window !== 'undefined') {
    return (
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints > 0) ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
    );
  }
  return false;
};

// Configuración mejorada para el backend
const backendOptions = {
  enableMouseEvents: true,
  enableTouchEvents: true,
  enableKeyboardEvents: true,
  delayTouchStart: 200, // Aumentado para mejorar la precisión
  ignoreContextMenu: true,
  scrollAngleRanges: [
    { start: 300, end: 60 },
  ],
  touchSlop: 10, // Reducido para mayor sensibilidad
  enableHoverOutsideTarget: true,
  getDropTargetElementsAtPoint: (x, y, dropTargets) => {
    return dropTargets.filter(t => {
      const rect = t.getBoundingClientRect();
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
  }
};

// Seleccionar el backend adecuado con opciones
const dndBackend = isTouchDevice() 
  ? [TouchBackend, backendOptions] 
  : [HTML5Backend, {}];

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// =============================================================================================
// Estilos adaptados a modo claro/oscuro
// =============================================================================================
const AppContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeComponent'
})(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
    backgroundColor: isWallpaperMode
      ? 'transparent'
      : (theme.palette.mode === 'light' ? '#FFFFFF' : '#000000')
  };
});

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeComponent'
})(({ theme, activeComponent }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexGrow: 1,
    padding: activeComponent ? 0 : theme.spacing(2),
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    backgroundColor: isWallpaperMode ? 'transparent' : 'inherit',
    overflow:
      activeComponent &&
      ['product', 'cashflow', 'supplier', 'council', 'growth', 'profile'].includes(activeComponent)
        ? 'hidden'
        : 'auto',
    [theme.breakpoints.up('md')]: {
      padding: activeComponent ? 0 : theme.spacing(3)
    }
  };
});

const TopBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeComponent'
})(({ theme, activeComponent }) => ({
  display: 'flex',
  position: 'relative',
  zIndex: 1100,
  marginBottom: activeComponent ? 0 : theme.spacing(3),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    marginBottom: activeComponent ? 0 : theme.spacing(4)
  },
  '& .top-wrapper': {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  '& .navigation-row': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1400px',
    padding: '0 16px',
    height: '48px',
    opacity: 1,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    position: 'relative'
  },
  '& .navigation-controls': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2
  },
  '& .home-button': {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    left: theme.spacing(2),
    zIndex: 2
  },
  '& .component-title': {
    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '1.5rem',
    letterSpacing: '0.5px',
    lineHeight: '1.5',
    margin: '0 8px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem'
    }
  },
  '& .actions': {
    display: 'flex',
    gap: theme.spacing(1),
    position: 'absolute',
    right: theme.spacing(2),
    zIndex: 2
  },
  '& .search-container': {
    width: '100%',
    maxWidth: '800px',
    padding: '0 16px',
    marginTop: theme.spacing(2)
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.text.primary,
    maxWidth: '1400px',
    padding: theme.spacing(0, 2)
  };
});

const Separator = styled(Box)(({ theme }) => ({
  maxWidth: '1400px',
  margin: '40px auto',
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(2, 'auto')
  },
  height: '1px',
  background: `linear-gradient(to right, 
    ${alpha(theme.palette.text.primary, 0.1)}, 
    ${alpha(theme.palette.text.primary, 0.15)}, 
    ${alpha(theme.palette.text.primary, 0.1)}
  )`
}));

const ShortcutsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2)
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    overflowX: 'auto'
  },
  '&::-webkit-scrollbar': {
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.text.primary, 0.1),
    borderRadius: '4px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.primary, 0.2),
    borderRadius: '4px'
  }
}));

const ShortcutGroup = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    minWidth: '300px'
  }
}));

const ShortcutTitle = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    color: isWallpaperMode ? '#FFFFFF' : theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1)
  };
});

const ShortcutsGrid = styled(Box)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'relative',
    backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : colors.cardBackground,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    overflowX: 'auto',
    alignItems: 'center',
    '&::-webkit-scrollbar': {
      height: '6px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: alpha(theme.palette.text.primary, 0.1),
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(theme.palette.text.primary, 0.2),
      borderRadius: '4px'
    },
    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      overflowX: 'hidden',
      alignItems: 'flex-start'
    }
  };
});

const ActionButtonWrapper = styled(Box)(({ theme, color }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minWidth: '60px',
    '& .icon-wrapper': {
      background: `linear-gradient(
        to top,
        rgba(0, 0, 0, 0.4),
        transparent
      ), ${color || theme.palette.primary.main}`,
      borderRadius: '50%',
      width: 36,
      height: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& .action-text': {
      color: isWallpaperMode ? '#FFFFFF' : theme.palette.text.primary,
      fontSize: '0.75rem',
      textAlign: 'center',
      lineHeight: 1.2,
      marginTop: theme.spacing(0.5),
      minHeight: '2.4em'
    }
  };
});

const NotificationDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -4,
  right: -4,
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: 'red'
}));

// Mejora del ModuleContainer con scroll snap y gestión de espacios
const ModuleContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isEditMode' && prop !== 'isDraggingAny'
})(({ theme, isEditMode, isDraggingAny }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3)
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))'
  },
  // Añadimos scroll snap para mejor posicionamiento
  scrollSnapType: isEditMode ? 'both mandatory' : 'none',
  scrollBehavior: 'smooth',
  '& > div': {
    scrollSnapAlign: 'start',
    transition: isEditMode
      ? 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease'
      : 'transform 0.2s ease, opacity 0.2s ease',
  },
  ...(isDraggingAny && {
    '& > div:not([data-is-dragging="true"]):not([data-is-over="true"])': {
      opacity: 0.6,
      transform: 'scale(0.98)',
    }
  })
}));

const ModuleCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isEditMode' && prop !== 'color'
})(({ theme, color, isEditMode }) => ({
  position: 'relative',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  backgroundColor: color || theme.palette.primary.main,
  border: isEditMode ? `2px dashed ${theme.palette.background.paper}` : 'none',
  padding: 0,
  height: '100px',
  cursor: isEditMode ? 'grab' : 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  // Add user-select properties to prevent text selection in edit mode
  userSelect: isEditMode ? 'none' : 'text',
  WebkitUserSelect: isEditMode ? 'none' : 'text',
  MozUserSelect: isEditMode ? 'none' : 'text',
  msUserSelect: isEditMode ? 'none' : 'text',
  '&:hover': {
    transform: isEditMode ? 'scale(1)' : 'translateY(-2px)',
    boxShadow: isEditMode ? '0 8px 20px rgba(0,0,0,0.2)' : 'none'
  },
  '&:active': {
    cursor: isEditMode ? 'grabbing' : 'pointer',
  },
  [theme.breakpoints.up('sm')]: {
    height: '120px'
  },
  '& .content-wrapper': {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)`,
    zIndex: 1
  },
  '& .title-wrapper': {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    zIndex: 2,
    [theme.breakpoints.up('sm')]: {
      top: theme.spacing(2)
    }
  },
  '& .title': {
    color: '#fff',
    fontWeight: 500,
    fontSize: '1rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.25rem'
    }
  },
  '& .description': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.75rem',
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 2,
    lineHeight: 1.2,
    maxWidth: '90%',
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.875rem',
      bottom: theme.spacing(3),
      left: theme.spacing(3),
      right: theme.spacing(3),
      maxWidth: '80%'
    }
  },
  ...(isEditMode && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -10,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 30,
      height: 5,
      backgroundColor: 'rgba(255,255,255,0.7)',
      borderRadius: 3,
      zIndex: 10
    },
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    transform: 'scale(0.95)'
  }),
  
  '& .edit-indicator': {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  },
  
  '& .drag-handle': {
    width: 10,
    height: 2,
    backgroundColor: 'white',
    position: 'relative',
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      width: 10,
      height: 2,
      backgroundColor: 'white'
    },
    '&::before': {
      top: -3
    },
    '&::after': {
      top: 3
    }
  }
}));

const ContentSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'activeComponent'
})(({ theme, activeComponent }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: activeComponent ? 0 : '0 8px',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: isWallpaperMode ? 'transparent' : 'inherit',
    overflow: activeComponent ? 'hidden' : 'visible',
    height: activeComponent ? '100%' : 'auto',
    [theme.breakpoints.up('sm')]: {
      padding: activeComponent ? 0 : '0 16px'
    }
  };
});

// Actualización de los estilos globales para incluir las animaciones de Apple
const GlobalStyles = styled('style')(() => ({
  '@keyframes wiggle': {
    '0%': { transform: 'rotate(-0.8deg)' },
    '50%': { transform: 'rotate(0.8deg)' },
    '100%': { transform: 'rotate(-0.8deg)' }
  },
  '@keyframes grabEffect': {
    '0%': { transform: 'scale(0.95) rotate(0deg)' },
    '25%': { transform: 'scale(0.97) rotate(-0.8deg)' },
    '50%': { transform: 'scale(0.98) rotate(0deg)' },
    '75%': { transform: 'scale(0.97) rotate(0.8deg)' },
    '100%': { transform: 'scale(0.95) rotate(0deg)' }
  },
  '@keyframes shake': {
    '0%': { transform: 'translate(1px, 1px) rotate(0deg)' },
    '10%': { transform: 'translate(-1px, -1px) rotate(-0.5deg)' },
    '20%': { transform: 'translate(-1px, 0px) rotate(0.5deg)' },
    '30%': { transform: 'translate(1px, 1px) rotate(0deg)' },
    '40%': { transform: 'translate(1px, -1px) rotate(0.5deg)' },
    '50%': { transform: 'translate(-1px, 1px) rotate(-0.5deg)' },
    '60%': { transform: 'translate(-1px, 1px) rotate(0deg)' },
    '70%': { transform: 'translate(1px, 1px) rotate(-0.5deg)' },
    '80%': { transform: 'translate(-1px, -1px) rotate(0.5deg)' },
    '90%': { transform: 'translate(1px, 1px) rotate(0deg)' },
    '100%': { transform: 'translate(1px, -1px) rotate(-0.5deg)' }
  },
  '@keyframes inserted': {
    '0%': { transform: 'scale(0)' },
    '100%': { transform: 'scale(1)' }
  },
  '@keyframes removed': {
    '0%': { transform: 'scale(1)', opacity: 1 },
    '100%': { transform: 'scale(0)', opacity: 0 }
  }
}));

// Componente DragPreview mejorado para mejor feedback visual
const DragPreview = () => {
  const { isDragging, item, currentOffset, initialClientOffset, initialSourceClientOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    initialClientOffset: monitor.getInitialClientOffset(),
    initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));
 
  if (!isDragging || !currentOffset || !initialClientOffset || !initialSourceClientOffset) {
    return null;
  }
  
  // Calculamos el offset exacto dentro del elemento donde se hizo clic
  const clickOffsetX = initialClientOffset.x - initialSourceClientOffset.x;
  const clickOffsetY = initialClientOffset.y - initialSourceClientOffset.y;
  
  // Posicionamos el preview exactamente donde está el cursor
  const style = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 1500,
    left: currentOffset.x - clickOffsetX,
    top: currentOffset.y - clickOffsetY,
    width: item.width || 300,
    height: item.height || 120,
    transform: 'rotate(-2deg) scale(1.05)',  // Rotación y escala para mejor feedback
    opacity: 1,  // Más visible durante el arrastre
    boxShadow: '0 20px 40px rgba(0,0,0,0.6)',  // Sombra más pronunciada tipo Apple
    transition: 'transform 0.1s ease-out',  // Transición más rápida para seguir el cursor
  };
 
  return (
    <Paper
      sx={{
        ...style,
        backgroundColor: item.color || '#415B7E',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <div style={{ 
        position: 'absolute',
        top: '12px',
        left: '16px',
        right: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        zIndex: 2
      }}>
        <Typography 
          style={{ 
            color: '#fff', 
            fontWeight: 500, 
            fontSize: '1.25rem' 
          }}
        >
          {item.title || 'Módulo'}
        </Typography>
      </div>
      <Typography 
        style={{ 
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '0.875rem',
          position: 'absolute',
          bottom: '16px',
          left: '16px',
          right: '16px',
          zIndex: 2,
          lineHeight: 1.2,
          maxWidth: '90%'
        }}
      >
        {item.description || 'Arrastrando...'}
      </Typography>
      <div style={{ 
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: `linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)`,
        zIndex: 1
      }} />
    </Paper>
  );
};

// Componente DraggableModuleCard mejorado con lógica de arrastre estilo Apple
const DraggableModuleCard = ({ module, index, moveModule, isEditMode, handleProductClick }) => {
  const ref = useRef(null);
  
  // Estado para la animación de retorno y desplazamiento
  const [isReturning, setIsReturning] = useState(false);
  const [isInserting, setIsInserting] = useState(false);
  
  // Medimos las dimensiones reales del elemento
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDimensions({ 
        width: rect.width, 
        height: rect.height 
      });
    }
    
    // Actualizar dimensiones si cambia el tamaño de la ventana
    const handleResize = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setDimensions({ 
          width: rect.width, 
          height: rect.height 
        });
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); 

  // Estado para rastrear si cualquier módulo está siendo arrastrado
  const [{ isDragging }, drag] = useDrag({
    type: 'MODULE',
    item: () => {
      // Asegurarnos de tener las dimensiones actualizadas
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        return { 
          index, 
          id: module.id,
          title: module.title,
          description: module.description,
          color: module.color,
          width: rect.width,
          height: rect.height,
          originalPosition: { left: rect.left, top: rect.top }
        };
      }
      
      return { 
        index, 
        id: module.id,
        title: module.title,
        description: module.description,
        color: module.color,
        width: dimensions.width,
        height: dimensions.height
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => isEditMode,
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        // Si no se soltó en un objetivo válido, animar el retorno
        setIsReturning(true);
        setTimeout(() => setIsReturning(false), 300); // Duración de la animación
      }
    },
  });
 
  const [{ isOver }, drop] = useDrop({
    accept: 'MODULE',
    hover: (item, monitor) => {
      if (!ref.current || !isEditMode) {
        return;
      }
 
      const dragIndex = item.index;
      const hoverIndex = index;
 
      // No reemplazar elementos consigo mismos
      if (dragIndex === hoverIndex) {
        return;
      }
      
      // Determinar la dirección del movimiento para la animación
      const direction = dragIndex > hoverIndex ? 'up' : 'down';
      
      // Obtener rectángulo del elemento actual
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      
      // Obtener punto medio vertical
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      
      // Calcular posición del puntero
      const clientOffset = monitor.getClientOffset();
      
      // Distancia desde el borde superior del elemento
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      // Solo realizar el movimiento cuando el cursor cruza la mitad del elemento
      // Esto crea un efecto más natural de "empujar" los elementos
      if (
        (direction === 'down' && hoverClientY < hoverMiddleY) ||
        (direction === 'up' && hoverClientY > hoverMiddleY)
      ) {
        return;
      }
 
      // Realizar el movimiento con la dirección para la animación
      moveModule(dragIndex, hoverIndex);
      
      // Mostrar animación de inserción
      setIsInserting(true);
      setTimeout(() => setIsInserting(false), 300);
 
      // Actualizar el índice del elemento arrastrado
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  });
 
  // Aplicar ambas referencias al elemento
  drag(drop(ref));

  // Si está siendo arrastrado, renderizar un placeholder casi invisible
  if (isDragging) {
    return (
      <div 
        ref={ref}
        style={{ 
          height: dimensions.height, 
          width: '100%',
          border: '2px dashed rgba(3, 133, 255, 0.3)',
          borderRadius: 16,
          backgroundColor: 'rgba(3, 133, 255, 0.05)',
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
        data-is-dragging="true"
      />
    );
  }
  
  // Si algo se está arrastrando sobre este elemento, mostrar un área de destino
  if (isOver) {
    return (
      <div 
        ref={ref}
        style={{ 
          height: dimensions.height, 
          width: '100%',
          border: '2px dashed #0385FF',
          borderRadius: 16,
          backgroundColor: 'rgba(3, 133, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transform: 'scale(1.02)',
        }}
        data-is-over="true"
      />
    );
  }

  // Estado normal - mostrar el módulo
  return (
    <div
      ref={ref}
      style={{
        opacity: 1,
        transform: isEditMode ? 'scale(0.95)' : 'scale(1)', 
        zIndex: isReturning ? 10 : 1,
        transition: isReturning 
          ? 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' 
          : isInserting 
            ? 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'transform 0.2s ease, opacity 0.2s ease',
        touchAction: isEditMode ? 'none' : 'auto',
        userSelect: isEditMode ? 'none' : 'text',
        WebkitUserSelect: isEditMode ? 'none' : 'text',
        MozUserSelect: isEditMode ? 'none' : 'text',
        msUserSelect: isEditMode ? 'none' : 'text',
        pointerEvents: 'auto',
        animationName: isEditMode ? 'jiggly' : 'none',
        animationTimingFunction: 'ease-in-out',
        animationDirection: 'alternate',
        animationIterationCount: 'infinite',
        animationDuration: 'var(--jiggle-duration, 150ms)',
        animationDelay: 'var(--jiggle-delay, 100ms)',
        willChange: 'transform',
        transformOrigin: 'center',
        // Efecto de inserción
        ...(isInserting && {
          animationName: 'inserted',
          animationDuration: '0.3s',
          animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
          animationFillMode: 'forwards',
        }),
      }}
      className="module-card"
    >
      <ModuleCard
        onClick={() => !isEditMode && handleProductClick(module.id)}
        color={module.color}
        isEditMode={isEditMode}
        sx={{
          transform: isEditMode ? 'scale(0.95)' : 'scale(1)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          boxShadow: isEditMode ? '0 8px 20px rgba(0,0,0,0.2)' : 'none',
          '&:hover': {
            transform: isEditMode ? 'scale(1.02)' : 'translateY(-2px)',
            boxShadow: isEditMode ? '0 12px 25px rgba(0,0,0,0.25)' : 'none'
          },
        }}
      >
        {isEditMode && (
          <div className="edit-indicator" style={{
            position: 'absolute',
            top: 8,
            left: 8,
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.25)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}>
            <div className="drag-handle" style={{
              width: 10,
              height: 2,
              backgroundColor: 'white',
              position: 'relative',
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                width: 10,
                height: 2,
                backgroundColor: 'white'
              },
              '&::before': {
                top: -3
              },
              '&::after': {
                top: 3
              }
            }}></div>
          </div>
        )}
        <div className="title-wrapper">
          <Typography className="title" variant="h6">
            {module.title}
          </Typography>
        </div>
        <Typography className="description">{module.description}</Typography>
        <div className="content-wrapper" />
      </ModuleCard>
    </div>
  );
};

// ---------------------------------------------------------------------------------------
// Componente principal: AGTUps
// ---------------------------------------------------------------------------------------
const AGTUps = ({ user }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Contexto de productos
  const { products, refreshProducts } = useContext(ProductContext);

  // Notificaciones para "Check Inventory"
  const stockProducts = (products || []).filter(
    (p) => p.category === 'physical' || p.category === 'food'
  );
  const nonStockProducts = (products || []).filter(
    (p) => !(p.category === 'physical' || p.category === 'food')
  );
  const userType =
    stockProducts.length === 0
      ? 'digital'
      : nonStockProducts.length === 0
      ? 'physical'
      : 'mixed';
  const hasInventoryNotification =
    (userType === 'physical' || userType === 'mixed') &&
    stockProducts.some((p) => (parseInt(p.stock, 10) || 0) < 10);

  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(
    () => localStorage.getItem('activeSection') || 'home'
  );
  const [companyProfile, setCompanyProfile] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [activeComponent, setActiveComponent] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // Estado para la suscripción
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  
  // Nuevos estados para control de reintentos y caché de suscripción
  const [subscriptionRetryCount, setSubscriptionRetryCount] = useState(0);
  const [lastSubscriptionCheck, setLastSubscriptionCheck] = useState(null);

  // Para "AGT Product" con flujo de tabs
  const [productFlow, setProductFlow] = useState({ initialTab: 0, triggerAdd: false });

  // Estado para el modo edición y arrastre
  const [isEditMode, setIsEditMode] = useState(false);
  const [orderedModules, setOrderedModules] = useState([]);
  const [originalModuleLayout, setOriginalModuleLayout] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDraggingAny, setIsDraggingAny] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // ==================== SHORTCUT MODALS (ESTADOS) ====================
  // ---------- PRODUCT ----------
  const [openSalesShortcutModal, setOpenSalesShortcutModal] = useState(false);
  const [openProductShortcutModal, setOpenProductShortcutModal] = useState(false);
  const [openCheckInventoryShortcutModal, setOpenCheckInventoryShortcutModal] = useState(false);
  const [openReviewPricesShortcutModal, setOpenReviewPricesShortcutModal] = useState(false);

  // ---------- CASHFLOW ----------
  const [openTransactionsShortcutModal, setOpenTransactionsShortcutModal] = useState(false);
  const [openQuickUploadShortcutModal, setOpenQuickUploadShortcutModal] = useState(false);
  const [openCurrentFlowShortcutModal, setOpenCurrentFlowShortcutModal] = useState(false);
  const [openViewBudgetsShortcutModal, setOpenViewBudgetsShortcutModal] = useState(false);

  // ---------- MARKETPLACE: 4 SHORTCUTS ----------
  const [openDealsShortcutModal, setOpenDealsShortcutModal] = useState(false);
  const [openNewSupplierModal, setOpenNewSupplierModal] = useState(false);
  const [openStoreModal, setOpenStoreModal] = useState(false);
  const [openStatsModal, setOpenStatsModal] = useState(false);

  // ---------- COUNCIL: 4 SHORTCUTS ----------
  const [openScheduleMeetingModal, setOpenScheduleMeetingModal] = useState(false);
  const [openRecentConsultationsModal, setOpenRecentConsultationsModal] = useState(false);
  const [openConsultationStatsModal, setOpenConsultationStatsModal] = useState(false);
  const [openScheduledSessionsModal, setOpenScheduledSessionsModal] = useState(false);

  // Inicializar el servicio de suscripción
  useEffect(() => {
    if (user) {
      subscriptionService.initialize({ uid: user.uid });
    }
  }, [user]);

  // Verificar el estado de la suscripción - VERSIÓN CORREGIDA
  useEffect(() => {
    // Si ya verificamos en los últimos 30 segundos, no volvemos a verificar
    const now = Date.now();
    if (lastSubscriptionCheck && now - lastSubscriptionCheck < 30000) {
      return;
    }
    
    const checkSubscription = async () => {
      if (!companyProfile) {
        setIsCheckingSubscription(false);
        return;
      }
    
      // Si ya intentamos 3 veces, no seguimos intentando
      if (subscriptionRetryCount >= 3) {
        console.warn('Subscription check failed after multiple attempts');
        setIsCheckingSubscription(false);
        setIsSubscriptionActive(false);
        return;
      }
  
      try {
        setIsCheckingSubscription(true);
        setLastSubscriptionCheck(now);
        const response = await subscriptionService.getSubscriptionStatus();
        setIsSubscriptionActive(response.data.isActive);
        // Reiniciar contador de intentos cuando hay éxito
        setSubscriptionRetryCount(0);
      } catch (error) {
        console.error('Error checking subscription:', error);
        setIsSubscriptionActive(false);
        // Incrementar contador de intentos fallidos
        setSubscriptionRetryCount(prev => prev + 1);
      } finally {
        setIsCheckingSubscription(false);
      }
    };
  
    checkSubscription();
  
    // Timeout de seguridad reducido a 5 segundos
    const timeoutId = setTimeout(() => {
      if (isCheckingSubscription) {
        console.warn('Subscription check timeout - forcing completion');
        setIsCheckingSubscription(false);
      }
    }, 5000);
  
    return () => clearTimeout(timeoutId);
  }, [companyProfile, subscriptionRetryCount, lastSubscriptionCheck, isCheckingSubscription]);

  // Función auxiliar para comparar dos layouts
  const areLayoutsEqual = (layout1, layout2) => {
    if (layout1.length !== layout2.length) return false;
   
    return layout1.every((module, index) => module.id === layout2[index].id);
  };

  // ---------------------------------------------------------------------------------------
  // Definición de arrays estáticos: Shortcuts y Modules usando useMemo
  // ---------------------------------------------------------------------------------------
  const shortcuts = useMemo(() => [
    {
      id: 'product',
      title: t('agtUps.modules.product.title'),
      color: '#415B7E',
      actions: [
        { icon: Plus, label: t('agtUps.modules.product.shortcuts.newProduct') },
        { icon: ShoppingCart, label: t('agtUps.modules.product.shortcuts.newSales') },
        { icon: Package, label: t('agtUps.modules.product.shortcuts.checkInventory') },
        { icon: DollarSign, label: t('agtUps.modules.product.shortcuts.reviewPrices') }
      ]
    },
    {
      id: 'council',
      title: t('agtUps.modules.council.title'),
      color: '#4A4F81',
      actions: [
        { icon: Calendar, label: t('agtUps.modules.council.shortcuts.scheduleMeeting') },
        { icon: MessageCircle, label: t('agtUps.modules.council.shortcuts.quickQuestion') },
        { icon: Clock, label: t('agtUps.modules.council.shortcuts.recentConsultations') },
        { icon: BarChart3, label: t('agtUps.modules.council.shortcuts.viewStats') }
      ]
    },
    {
      id: 'cashflow',
      title: t('agtUps.modules.cashflow.title'),
      color: '#DC9844',
      actions: [
        { icon: Upload, label: t('agtUps.modules.cashflow.shortcuts.quickReceipt') },
        { icon: Download, label: t('agtUps.modules.cashflow.shortcuts.currentFlow') },
        { icon: PieChart, label: t('agtUps.modules.cashflow.shortcuts.viewBudgets') },
        { icon: ArrowUpDown, label: t('agtUps.modules.cashflow.shortcuts.transactions') }
      ]
    },
    {
      id: 'supplier',
      title: t('agtUps.modules.marketplace.title'),
      color: '#25505A',
      actions: [
        { icon: MessageSquare, label: t('agtUps.modules.marketplace.shortcuts.activeDeals') },
        { icon: UserPlus, label: t('agtUps.modules.marketplace.shortcuts.newSupplier') },
        { icon: StoreIcon, label: t('agtUps.modules.marketplace.shortcuts.store') },
        { icon: ChartScatter, label: t('agtUps.modules.marketplace.shortcuts.stats') }
      ]
    }
  ], [t]);

  const modules = useMemo(() => [
    {
      id: 'product',
      title: t('agtUps.modules.product.title'),
      description: t('agtUps.modules.product.description'),
      color: '#415B7E',
      icon: BarChart3,
      component: AGTProduct,
      actions: shortcuts.find((s) => s.id === 'product')?.actions || []
    },
    {
      id: 'cashflow',
      title: t('agtUps.modules.cashflow.title'),
      description: t('agtUps.modules.cashflow.description'),
      color: '#DC9844',
      icon: Receipt,
      component: AGTCashFlow,
      actions: []
    },
    {
      id: 'supplier',
      title: t('agtUps.modules.marketplace.title'),
      description: t('agtUps.modules.marketplace.description'),
      color: '#25505A',
      icon: Users,
      component: AGTMarketplace,
      actions: []
    },
    {
      id: 'council',
      title: t('agtUps.modules.council.title'),
      description: t('agtUps.modules.council.description'),
      color: '#4A4F81',
      icon: Brain,
      component: AGTCouncil,
      actions: []
    },
    {
      id: 'growth',
      title: t('agtUps.modules.growth.title'),
      description: t('agtUps.modules.growth.description'),
      color: '#FFB692',
      icon: TrendingUp,
      component: AGTGrowth,
      actions: []
    },
    {
      id: 'profile',
      title: t('agtUps.modules.profile.title'),
      description: t('agtUps.modules.profile.description'),
      color: '#BC4C4A',
      icon: Building2,
      component: AGTProfile,
      actions: []
    }
  ], [t, shortcuts]);

  // Inicializar los módulos ordenados
  useEffect(() => {
    setOrderedModules([...modules]);
    setOriginalModuleLayout([...modules]);
  }, [modules]);

  // Define loadModuleLayout function before using it in useEffect
  const loadModuleLayout = useCallback(async () => {
    if (!profileId) return;
   
    try {
      const result = await profileService.getProfilePreferences(profileId);
      if (result.success && result.data?.preferences?.moduleLayout?.length > 0) {
        const savedLayout = result.data.preferences.moduleLayout;
   
        // Reconstruir el array de módulos ordenados usando solo los IDs guardados
        const reconstructedModules = savedLayout
          .map(savedModule => {
            // Encontrar el módulo completo con traducciones actuales usando el ID
            const fullModule = modules.find(m => m.id === savedModule.id);
            return fullModule || null;
          })
          .filter(Boolean); // Eliminar cualquier módulo no encontrado
   
        // Si hay módulos válidos, actualizar el estado
        if (reconstructedModules.length > 0) {
          setOrderedModules(reconstructedModules);
   
          // Guardar una versión simplificada para comparar después
          const simplifiedLayout = reconstructedModules.map(module => ({
            id: module.id
          }));
          setOriginalModuleLayout(JSON.parse(JSON.stringify(simplifiedLayout)));
        } else {
          // Si no hay módulos válidos, usar el predeterminado
          setOrderedModules([...modules]);
          setOriginalModuleLayout(modules.map(m => ({ id: m.id })));
        }
      } else {
        // Si no hay layout guardado, usar el predeterminado
        setOrderedModules([...modules]);
        setOriginalModuleLayout(modules.map(m => ({ id: m.id })));
      }
    } catch (error) {
      console.error("Error loading module layout:", error);
      // En caso de error, usar el layout predeterminado
      setOrderedModules([...modules]);
      setOriginalModuleLayout(modules.map(m => ({ id: m.id })));
    }
  }, [modules, profileId]);
  
  // Now use it in useEffect
  useEffect(() => {
    if (profileId) {
      loadModuleLayout();
    }
  }, [profileId, loadModuleLayout]);

  // Función para guardar la disposición actual
  const saveModuleLayout = async () => {
    if (!profileId) {
      setSnackbar({
        open: true,
        message: 'Unable to save: Profile not loaded',
        severity: 'error'
      });
      return;
    }
   
    setIsSaving(true);
    try {
      // Crear una versión simplificada del layout que solo contenga IDs
      const simplifiedLayout = orderedModules.map(module => ({
        id: module.id
      }));
   
      const result = await profileService.saveModuleLayout(profileId, simplifiedLayout);
      if (result.success) {
        // Actualizar el layout original con el nuevo guardado (también simplificado)
        setOriginalModuleLayout(JSON.parse(JSON.stringify(simplifiedLayout)));
        setHasChanges(false);
   
        // Añadir una animación suave para confirmar visualmente el guardado
        const modules = document.querySelectorAll('.module-card');
        modules.forEach(module => {
          module.animate([
            { transform: 'scale(1)', opacity: 1 },
            { transform: 'scale(0.95)', opacity: 0.8 },
            { transform: 'scale(1.02)', opacity: 1 },
            { transform: 'scale(1)', opacity: 1 }
          ], {
            duration: 600,
            easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
          });
        });
   
        setSnackbar({
          open: true,
          message: 'Module layout saved successfully',
          severity: 'success'
        });
      } else {
        throw new Error(result.error?.message || 'Error saving layout');
      }
    } catch (error) {
      console.error("Error saving module layout:", error);
      setSnackbar({
        open: true,
        message: 'Failed to save layout: ' + error.message,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Monitorear el estado de arrastre global
  useEffect(() => {
    const handleDragStart = () => setIsDraggingAny(true);
    const handleDragEnd = () => setIsDraggingAny(false);

    // Añadir listeners globales
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);

    return () => {
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, []);

  // Función para mover un módulo durante el arrastre (actualizada para detectar cambios)
  const moveModule = useCallback((dragIndex, hoverIndex) => {
    setOrderedModules((prevModules) => {
      const updatedModules = [...prevModules];
      const draggedItem = updatedModules[dragIndex];
      
      // Eliminar del índice actual
      updatedModules.splice(dragIndex, 1);
      
      // Insertar en el nuevo índice
      updatedModules.splice(hoverIndex, 0, draggedItem);
      
      // Comprobar si hay cambios comparando con el layout original
      const hasLayoutChanged = !areLayoutsEqual(updatedModules, originalModuleLayout);
      setHasChanges(hasLayoutChanged);
      
      return updatedModules;
    });
  }, [originalModuleLayout]);

  // Función para alternar el modo edición
  const toggleEditMode = () => {
    if (isEditMode) {
      // Si hay cambios, guardar; si no, simplemente salir del modo edición
      if (hasChanges) {
        saveModuleLayout();
      } else {
        console.log("No changes detected, skipping API call");
      }
    } else {
      // Al entrar en modo edición, resetear el flag de cambios
      setHasChanges(false);
    }
    setIsEditMode(!isEditMode);
  };

  // ===============================================================
  // Manejo de perfil (verificamos que tenga un perfil antes de mostrar "home")
  // ===============================================================
  const handleProfileUpdate = (updatedProfile) => {
    setCompanyProfile(updatedProfile);
    if (updatedProfile?.profileId) {
      setProfileId(updatedProfile.profileId);

      // Después de actualizar el perfil, verificar la suscripción
      checkSubscriptionAfterProfile();
    }
  };

  // Verificar suscripción después de crear/actualizar perfil
  const checkSubscriptionAfterProfile = async () => {
    try {
      setIsCheckingSubscription(true);
      const now = Date.now();
      setLastSubscriptionCheck(now);
      
      const response = await subscriptionService.getSubscriptionStatus();
      setIsSubscriptionActive(response.data.isActive);
      
      // Si no tiene una suscripción activa, cambiar a la sección principal
      // para que se muestre el componente SubscriptionGateway
      if (!response.data.isActive) {
        setActiveSection('home'); // Cambiamos a 'home' para que renderContent() muestre SubscriptionGateway
        localStorage.setItem('activeSection', 'home');
      } else {
        // Si ya tiene suscripción, ir directamente a home
        setActiveSection('home');
        localStorage.setItem('activeSection', 'home');
      }
    } catch (error) {
      console.error('Error checking subscription after profile update:', error);
      setIsSubscriptionActive(false);
      
      // En caso de error, también cambiamos a 'home' para mostrar SubscriptionGateway
      setActiveSection('home');
      localStorage.setItem('activeSection', 'home');
      
      // Incrementar contador de reintentos
      setSubscriptionRetryCount(prev => prev + 1);
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  // Manejar la finalización de la suscripción
  const handleSubscriptionComplete = () => {
    setIsSubscriptionActive(true);
    setActiveSection('home');
    localStorage.setItem('activeSection', 'home');
    
    // Reiniciar contador de reintentos cuando la suscripción se completa
    setSubscriptionRetryCount(0);
  };

  // Cargar perfil desde el backend
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const { exists, isNewProfile, data } = await profileService.getProfile();

        if (exists && !isNewProfile && data) {
          setCompanyProfile(data);
          setProfileId(data.profileId);
        } else {
          setActiveSection('profile');
          localStorage.setItem('activeSection', 'profile');
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setActiveSection('profile');
        localStorage.setItem('activeSection', 'profile');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadProfile();
    }
  }, [user]);

  // Función para recortar el título en mobile
  const getResponsiveTitle = useCallback((fullTitle, isMobile) => {
    if (!isMobile) return fullTitle;
    
    // Buscamos el módulo correspondiente al título
    const module = modules.find(m => m.title === fullTitle);
    if (module) {
      return t(`agtUps.modules.${module.id}.shortTitle`);
    }
    
    return fullTitle;
  }, [modules, t]);

  // Búsqueda
  const handleSearchChange = (newQuery) => {
    setSearchQuery(newQuery);
  };

  const handleNavigate = useCallback(
    (direction) => {
      const currentIndex = modules.findIndex((p) => p.id === activeComponent);
      let nextIndex;
      if (direction === 'next') {
        nextIndex = currentIndex + 1 >= modules.length ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex - 1 < 0 ? modules.length - 1 : currentIndex - 1;
      }
      setActiveComponent(modules[nextIndex].id);
    },
    [activeComponent, modules]
  );

  const handleHome = useCallback(() => {
    setActiveComponent(null);
    setSearchResults([]);
    setSearchQuery('');
  }, []);

  const handleProductClick = useCallback((moduleId) => {
    if (moduleId === 'product') {
      setProductFlow((prev) => ({ ...prev, triggerAdd: false }));
    }
    setActiveComponent(moduleId);
  }, []);

  const toggleFavorite = useCallback((moduleId) => {
    setFavorites((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  }, []);

  const handleResultClick = (result) => {
    setActiveComponent(result.id);
  };

  const handleSearchResults = useCallback(async (result) => {
    if (result.error) {
      console.error(result.error);
      return;
    }
    setIsSearching(true);
    try {
      if (result.recommendations) {
        setSearchResults(result.recommendations);
        setActiveComponent('searchResults');
      }
    } catch (error) {
      console.error('Error processing search results:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // ===============================================================
  // Handlers para PRODUCT shortcuts
  // ===============================================================
  const handleProductShortcut = useCallback((actionLabel) => {
    if (actionLabel === t('agtUps.modules.product.shortcuts.newProduct')) {
      setOpenProductShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.product.shortcuts.newSales')) {
      setOpenSalesShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.product.shortcuts.checkInventory')) {
      setOpenCheckInventoryShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.product.shortcuts.reviewPrices')) {
      setOpenReviewPricesShortcutModal(true);
    }
  }, [t]);

  // ===============================================================
  // Handlers para CASH FLOW shortcuts
  // ===============================================================
  const handleCashFlowShortcut = useCallback((actionLabel) => {
    if (actionLabel === t('agtUps.modules.cashflow.shortcuts.quickReceipt')) {
      setOpenQuickUploadShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.cashflow.shortcuts.currentFlow')) {
      setOpenCurrentFlowShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.cashflow.shortcuts.viewBudgets')) {
      setOpenViewBudgetsShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.cashflow.shortcuts.transactions')) {
      setOpenTransactionsShortcutModal(true);
    }
  }, [t]);

  // ===============================================================
  // Handlers para COUNCIL shortcuts
  // ===============================================================
  const handleCouncilShortcut = useCallback((actionLabel) => {
    if (actionLabel === t('agtUps.modules.council.shortcuts.scheduleMeeting')) {
      setOpenScheduleMeetingModal(true);
    } else if (actionLabel === t('agtUps.modules.council.shortcuts.quickQuestion')) {
      setActiveComponent('council');
    } else if (actionLabel === t('agtUps.modules.council.shortcuts.recentConsultations')) {
      setOpenRecentConsultationsModal(true);
    } else if (actionLabel === t('agtUps.modules.council.shortcuts.viewStats')) {
      setOpenConsultationStatsModal(true);
    }
  }, [t]);

  const handleResetProductFlowTrigger = useCallback(() => {
    setProductFlow((prev) => ({ ...prev, triggerAdd: false }));
  }, []);

  // ===============================================================
  // Handlers para MARKETPLACE shortcuts
  // ===============================================================
  const handleMarketplaceShortcut = useCallback((actionLabel) => {
    if (actionLabel === t('agtUps.modules.marketplace.shortcuts.activeDeals')) {
      setOpenDealsShortcutModal(true);
    } else if (actionLabel === t('agtUps.modules.marketplace.shortcuts.newSupplier')) {
      setOpenNewSupplierModal(true);
    } else if (actionLabel === t('agtUps.modules.marketplace.shortcuts.store')) {
      setOpenStoreModal(true);
    } else if (actionLabel === t('agtUps.modules.marketplace.shortcuts.stats')) {
      setOpenStatsModal(true);
    } else {
      console.log(`Shortcut: ${actionLabel} from Marketplace group`);
    }
  }, [t]);

  // ===============================================================
  // Render principal
  // ===============================================================
  const renderContent = () => {
    // Forzamos que si no hay perfil, muestre la vista de "AGTProfile"
    if (activeSection === 'profile') {
      return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
          <AGTProfile user={user} onProfileUpdate={handleProfileUpdate} />
        </Box>
      );
    }

    // Si hay perfil pero no suscripción activa, mostrar la vista de suscripción
    if (companyProfile && isSubscriptionActive === false) {
      return <SubscriptionGateway onSubscriptionComplete={handleSubscriptionComplete} />;
    }
    
    // Si está cargando la verificación de suscripción, mostrar spinner
    if (isCheckingSubscription) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <AppleSpinner sizeDesktop={60} sizeMobile={40} />
        </Box>
      );
    }

    // Resultados de búsqueda
    if (activeComponent === 'searchResults') {
      return (
        <AGTFoundResults
          results={searchResults}
          isLoading={isSearching}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchResults={handleSearchResults}
          onResultClick={handleResultClick}
          setActiveComponent={setActiveComponent}
          user={user}
        />
      );
    }

    // Verificamos si se ha seleccionado un "module"
    const activeModule = modules.find((p) => p.id === activeComponent);
    if (activeModule) {
      const Component = activeModule.component;
      // AGT Product con props adicionales
      if (activeModule.id === 'product') {
        return (
          <Component
            user={user}
            initialTab={productFlow.initialTab}
            triggerAdd={productFlow.triggerAdd}
            refreshProducts={refreshProducts}
            onResetTriggerAdd={handleResetProductFlowTrigger}
          />
        );
      }
      // Resto de módulos
      return <Component user={user} />;
    }

    // ================ Sección "Home" (Shortcuts + Suggestions) ================
    return (
      <ContentSection>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <SectionTitle sx={{ mb: 2 }}>{t('agtUps.home.shortcuts')}</SectionTitle>
        </motion.div>

        <ShortcutsContainer>
          {shortcuts.map((group, groupIndex) => (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: groupIndex * 0.08,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
            >
              <ShortcutGroup>
                <ShortcutTitle>{group.title}</ShortcutTitle>
                <Box position="relative">
                  <ShortcutsGrid>
                    {group.actions.map((action, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          duration: 0.3,
                          delay: (groupIndex * 0.08) + (index * 0.05),
                          ease: [0.25, 0.46, 0.45, 0.94]
                        }}
                      >
                        <ActionButtonWrapper
                          color={group.color}
                          onClick={() => {
                            if (group.id === 'product') {
                              handleProductShortcut(action.label);
                            } else if (group.id === 'cashflow') {
                              handleCashFlowShortcut(action.label);
                            } else if (group.id === 'supplier') {
                              handleMarketplaceShortcut(action.label);
                            } else if (group.id === 'council') {
                              handleCouncilShortcut(action.label);
                            } else {
                              console.log(`Shortcut: ${action.label} (group: ${group.id})`);
                            }
                          }}
                        >
                          <div className="icon-wrapper">
                            <action.icon size={20} color="white" />
                            {group.id === 'product' &&
                              action.label === t('agtUps.modules.product.shortcuts.checkInventory') &&
                              hasInventoryNotification && <NotificationDot />}
                          </div>
                          <Typography className="action-text">{action.label}</Typography>
                        </ActionButtonWrapper>
                      </motion.div>
                    ))}
                  </ShortcutsGrid>
                  {/* Se ha eliminado el overlay de bloqueo para que todos los atajos estén desbloqueados */}
                </Box>
              </ShortcutGroup>
            </motion.div>
          ))}
        </ShortcutsContainer>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <Separator />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <SectionTitle>{t('agtUps.home.suggestions')}</SectionTitle>
            <Box sx={{ marginRight: theme => theme.spacing(2) }}>
            <IconButton
              onClick={toggleEditMode}
              disabled={isSaving}
              sx={{
                backgroundColor: isEditMode ? 'green' : 'transparent',
                color: isEditMode ? 'white' : 'inherit',
                '&:hover': {
                  backgroundColor: isEditMode
                    ? 'darkgreen' // Darker green when hovering in edit mode
                    : (theme) => alpha(theme.palette.text.primary, 0.1), // Slight background in normal mode
                },
                transition: 'background-color 0.2s ease'
              }}
            >
              {isEditMode ?
                (isSaving ? <AppleSpinner sizeDesktop={24} sizeMobile={20} /> : <Check />) :
                <Wand2 />
              }
            </IconButton>
            </Box>
          </Box>
        </motion.div>

        <DndProvider backend={dndBackend[0]} options={dndBackend[1]}>
          {/* Componente de vista previa de arrastre */}
          <DragPreview />

          <ModuleContainer isEditMode={isEditMode} isDraggingAny={isDraggingAny} className={isEditMode ? 'edit-mode-active' : ''}>
            {orderedModules.map((module, index) => (
              <motion.div
                key={`${module.id}-${index}-motion`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.6 + (index * 0.08),
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              >
                <DraggableModuleCard
                  module={module}
                  index={index}
                  moveModule={moveModule}
                  isEditMode={isEditMode}
                  handleProductClick={handleProductClick}
                />
              </motion.div>
            ))}
          </ModuleContainer>
        </DndProvider>
      </ContentSection>
    );
  };

  // Loading - Mostrar mientras carga el perfil
  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
         <AppleSpinner sizeDesktop={60} sizeMobile={40} />
      </Box>
    );
  }

  // ===============================================================
  // Render final
  // ===============================================================
  return (
    <AppContainer activeComponent={activeComponent}>
      {/* Incluimos los estilos globales para la animación de wiggle y prevenir selección de texto */}
      <GlobalStyles>{`
        @keyframes jiggly {
          0% {
            transform: rotate(-0.3deg);
          }
          to {
            transform: rotate(0.3deg);
          }
        }
        
        @keyframes inserted {
          0% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        @keyframes removed {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0);
            opacity: 0;
          }
        }
      
        .grabbing {
          cursor: grabbing !important;
        }
      
        ${isEditMode ? `
        .edit-mode-active * {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
        ` : ''}
      
        /* Definir variables de retraso y duración para diferentes elementos */
        .edit-mode-active > div:nth-child(5n) {
          --jiggle-delay: 100ms;
          --jiggle-duration: 120ms;
        }
      
        .edit-mode-active > div:nth-child(5n+2) {
          --jiggle-delay: 110ms;
          --jiggle-duration: 130ms;
        }
      
        .edit-mode-active > div:nth-child(5n+4) {
          --jiggle-delay: 120ms;
          --jiggle-duration: 140ms;
        }
      
        .edit-mode-active > div:nth-child(5n+1) {
          --jiggle-delay: 130ms;
          --jiggle-duration: 150ms;
        }
      
        .edit-mode-active > div:nth-child(5n+3) {
          --jiggle-delay: 140ms;
          --jiggle-duration: 160ms;
        }
        
        /* Transiciones fluidas para reorganización */
        .edit-mode-active > div {
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), 
                    opacity 0.2s ease,
                    left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
                    top 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          will-change: transform, opacity, left, top;
        }
      
        /* Deshabilitar animación si se prefiere reducir el movimiento */
        @media (prefers-reduced-motion: reduce) {
          .edit-mode-active > div {
            animation-name: none !important;
            transition: none !important;
          }
        }
      `}</GlobalStyles>
      
      <ContentContainer activeComponent={activeComponent}>
        <TopBar activeComponent={activeComponent}>
          <div className="top-wrapper">
            {activeComponent && (
              <div className="navigation-row">
                {/* Botón Home */}
                <div
                  className="home-button"
                  onClick={handleHome}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  <ChevronLeft
                    size={20}
                    style={{
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      ml: 1,
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                    }}
                  >
                    {t('agtUps.common.navigation.home')}
                  </Typography>
                </div>
                {/* Navegación Prev/Next (excepto en Search) */}
                {activeComponent !== 'searchResults' ? (
                  <div className="navigation-controls">
                    <IconButton onClick={() => handleNavigate('prev')}>
                      <ChevronLeft
                        style={{
                          color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                        }}
                      />
                    </IconButton>
                    <Typography className="component-title">
                      {getResponsiveTitle(
                        modules.find((p) => p.id === activeComponent)?.title,
                        isMobile
                      )}
                    </Typography>
                    <IconButton onClick={() => handleNavigate('next')}>
                      <ChevronRight
                        style={{
                          color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                        }}
                      />
                    </IconButton>
                  </div>
                ) : (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Typography className="component-title">{t('agtUps.common.navigation.search')}</Typography>
                  </div>
                )}
                {/* Favorito */}
                <div className="actions">
                  <IconButton
                    onClick={() => toggleFavorite(activeComponent)}
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit'
                    }}
                  >
                    <Star fill={favorites.includes(activeComponent) ? 'currentColor' : 'none'} />
                  </IconButton>
                </div>
              </div>
            )}
            {/* Cuadro de búsqueda (solo si no hay módulo activo) */}
            {!activeComponent && activeSection !== 'profile' && isSubscriptionActive && (
              <Fade in={true}>
                <Box className="search-container">
                  <AGTFound
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onResults={handleSearchResults}
                    placeholder={t('agtUps.common.search.placeholder')}
                    user={user}
                  />
                </Box>
              </Fade>
            )}
          </div>
        </TopBar>

        {activeComponent ? (
          <Box
            sx={(theme) => {
              const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
              return {
                flexGrow: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: isWallpaperMode ? 'transparent' : 'inherit'
              };
            }}
          >
            {renderContent()}
          </Box>
        ) : (
          <ContentSection activeComponent={activeComponent}>{renderContent()}</ContentSection>
        )}
      </ContentContainer>

      {/* ====================== MODALES DE SHORTCUTS ====================== */}
      {/* ---- PRODUCT shortcuts ---- */}
      <SalesStrategyShortcut
        open={openSalesShortcutModal}
        onClose={() => setOpenSalesShortcutModal(false)}
      />
      <ProductShortcut
        open={openProductShortcutModal}
        onClose={() => setOpenProductShortcutModal(false)}
      />
      <CheckInventoryShortcut
        open={openCheckInventoryShortcutModal}
        onClose={() => setOpenCheckInventoryShortcutModal(false)}
      />
      <ReviewPricesShortcut
        open={openReviewPricesShortcutModal}
        onClose={() => setOpenReviewPricesShortcutModal(false)}
      />

      {/* ---- CASH FLOW shortcuts ---- */}
      <TransactionsShortcut
        open={openTransactionsShortcutModal}
        onClose={() => setOpenTransactionsShortcutModal(false)}
      />
      <QuickUploadShortcut
        open={openQuickUploadShortcutModal}
        onClose={() => setOpenQuickUploadShortcutModal(false)}
      />
      <CurrentFlowShortcut
        open={openCurrentFlowShortcutModal}
        onClose={() => setOpenCurrentFlowShortcutModal(false)}
      />
      <ViewBudgetsShortcut
        open={openViewBudgetsShortcutModal}
        onClose={() => setOpenViewBudgetsShortcutModal(false)}
      />

      {/* ---- MARKETPLACE: 4 atajos ---- */}
      <ActiveDealsShortcut
        open={openDealsShortcutModal}
        onClose={() => setOpenDealsShortcutModal(false)}
      />
      <NewSupplierShortcut
        open={openNewSupplierModal}
        onClose={() => setOpenNewSupplierModal(false)}
      />
      <StoreShortcut 
        open={openStoreModal} 
        onClose={() => setOpenStoreModal(false)} 
        products={products} 
      />
      <StatsShortcut 
        open={openStatsModal} 
        onClose={() => setOpenStatsModal(false)} 
      />

      {/* ---- COUNCIL: 4 atajos ---- */}
      <ScheduleMeetingShortcut
        open={openScheduleMeetingModal}
        onClose={() => setOpenScheduleMeetingModal(false)}
      />
      <RecentConsultationsShortcut
        open={openRecentConsultationsModal}
        onClose={() => setOpenRecentConsultationsModal(false)}
      />
      <ConsultationStatsShortcut
        open={openConsultationStatsModal}
        onClose={() => setOpenConsultationStatsModal(false)}
      />
      <ScheduledSessionsShortcut
        open={openScheduledSessionsModal}
        onClose={() => setOpenScheduledSessionsModal(false)}
      />

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppContainer>
  );
};

export default AGTUps;